import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import httpFetch from '@/lib/http'
import { fetchUserInfo, UserInfo } from '@/lib/api/user'

interface User {
  id: string
  email: string
  name: string
  username?: string  // 新增username字段
  avatar?: string
  plan: 'free' | 'pro' | 'enterprise'
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  
  // Actions
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  setToken: (token: string) => void
  refreshToken: () => Promise<void>
  // SSO相关方法
  initializeFromSSO: () => void
  // 用户信息获取
  fetchUserInfo: () => Promise<void>
  // 获取username
  getUsername: () => string | undefined
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          // 模拟网络延迟
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // 测试环境下的模拟登录
          if (import.meta.env.DEV) {
             // 演示账户验证
             if (email === 'demo@memecho.com' && password === 'demo123') {
               const mockUser: User = {
                 id: 'demo-user-1',
                 email: 'demo@memecho.com',
                 name: 'Demo User',
                 avatar: undefined,
                 plan: 'pro'
               }
              const mockToken = 'mock-jwt-token-demo-user'
              
              set({
                user: mockUser,
                token: mockToken,
                isAuthenticated: true,
                isLoading: false,
              })
              return
            }
            
             // 其他测试账户
             if (email === 'admin@memecho.com' && password === 'admin123') {
               const mockUser: User = {
                 id: 'admin-user-1',
                 email: 'admin@memecho.com',
                 name: 'Admin User',
                 avatar: undefined,
                 plan: 'enterprise'
               }
              const mockToken = 'mock-jwt-token-admin-user'
              
              set({
                user: mockUser,
                token: mockToken,
                isAuthenticated: true,
                isLoading: false,
              })
              return
            }
            
            // 测试失败情况
            set({ isLoading: false })
            throw new Error('Invalid credentials')
          }
          
          // 生产环境下的实际API调用
          const response = await httpFetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
            // 登录接口不应自动附带 Authorization
            skipAuth: true,
          })
          
          if (!response.ok) {
            throw new Error('Login failed')
          }
          
          const { user, token } = await response.json()
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      logout: () => {
        // 清除localStorage中的token
        localStorage.removeItem('access_token')
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        })
      },

      setUser: (user: User) => {
        set({ user })
      },

      setToken: (token: string) => {
        set({ token, isAuthenticated: !!token })
      },

      refreshToken: async () => {
        const { token } = get()
        if (!token) return

        try {
          const response = await httpFetch('/api/auth/refresh', {
            headers: { Authorization: `Bearer ${token}` },
          })
          
          if (response.ok) {
            const { token: newToken } = await response.json()
            set({ token: newToken })
          } else {
            // Token 过期，清除认证状态
            get().logout()
          }
        } catch (error) {
          get().logout()
        }
      },

      initializeFromSSO: () => {
        const token = localStorage.getItem('access_token')
        if (token) {
          // 检查是否已经有用户信息，避免重复解析
          const { user: existingUser } = get()
          if (existingUser && existingUser.id !== 'sso-user') {
            console.log('用户信息已存在，跳过重复解析')
            return
          }
          
          // 尝试从token中解析用户信息
          try {
            console.log('开始从token解析用户信息...')
            const parts = token.split('.')
            if (parts.length >= 2) {
              const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
              const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=')
              const jsonPayload = decodeURIComponent(
                atob(padded)
                  .split('')
                  .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                  .join('')
              )
              const claims = JSON.parse(jsonPayload) as Record<string, unknown>
              
              console.log('JWT claims:', claims)
              console.log('检测到username字段:', claims['username'])
              console.log('检测到name字段:', claims['name'])
              console.log('检测到email字段:', claims['email'])
              console.log('所有可用字段:', Object.keys(claims))
              
              const planClaim = (claims['plan'] as string) || 'pro'
              const normalizedPlan = ['free', 'pro', 'enterprise'].includes(planClaim) 
                ? (planClaim as 'free' | 'pro' | 'enterprise') 
                : 'pro'
              
              const user: User = {
                id: (claims['sub'] as string) || (claims['id'] as string) || 'sso-user',
                email: (claims['email'] as string) || 'sso@memecho.com',
                name: (claims['name'] as string) || (claims['username'] as string) || (claims['email'] as string)?.split('@')[0] || 'User',
                username: (claims['username'] as string) || undefined,  // 提取username字段
                avatar: (claims['picture'] as string) || undefined,
                plan: normalizedPlan,
              }
              
              console.log('解析出的用户信息:', user)
              
              set({
                user,
                token,
                isAuthenticated: true,
              })
              
              console.log('✅ 用户信息解析和存储完成')
            }
          } catch (error) {
            console.error('❌ token解析失败:', error)
            // token解析失败，只设置token
            set({ token, isAuthenticated: true })
          }
        }
      },

      fetchUserInfo: async () => {
        const { token, isAuthenticated } = get()
        if (!token || !isAuthenticated) {
          throw new Error('User not authenticated')
        }

        set({ isLoading: true })
        try {
          const userInfo = await fetchUserInfo()
          
          // 转换UserInfo为User格式
          const user: User = {
            id: userInfo.id,
            email: userInfo.email,
            name: userInfo.name,
            username: userInfo.username,
            avatar: userInfo.avatar,
            plan: userInfo.plan || 'free',
          }
          
          set({ user, isLoading: false })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      getUsername: () => {
        const { user } = get()
        return user?.username
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
